/**
 * Per-speaker geometry — the Genelec GLM/Trinnov-style "one line per
 * channel" view. Each speaker's own complex contribution to the room's
 * combined frequency response was always computed as an intermediate value
 * in the backend's summing loop; this is a pure reshape of that data, not
 * new physics (see FreqResponseResponse.per_speaker).
 *
 * Deliberately does NOT apply the mean-shift the combined FreqResponse
 * chart uses for its active/baseline comparison — that shift exists to make
 * a LEVEL DELTA between two different room states legible. Here every
 * speaker shares the same physical reference (P_REF, magToDb), so their
 * ABSOLUTE relative levels are exactly what the chart needs to show: is one
 * speaker or sub quieter than the others at the listener. Shifting each to
 * its own mean would erase the very thing this chart exists to reveal.
 */

import { createPlotFrame, DEFAULT_CHART_PAD, type ChartPad, type PlotFrame } from './layout';
import { createLinearScale, createLogFreqScale, linearToPlot, type LinearScale, type LogFreqScale } from './scales';
import { dbViewportFromSeries, magToDb, mapSeriesToPlot, yDomainFromSeries, type PlotPoint } from './series';
import { linearTicks, niceLinearTicks } from './ticks';
import { computeXAxisTicks, defaultMeasureWidth, type AxisTicks, type YTick } from './geometry';
import { unwrapPhase } from './phase';

export interface PerSpeakerInput {
  index: number;
  subkind: string | null;
  /** Linear pressure magnitude, for the FR geometry. */
  mag?: number[];
  /** Wrapped radians (np.angle), for the phase geometry. */
  phase?: number[];
}

export interface PerSpeakerSeries {
  index: number;
  subkind: string | null;
  points: PlotPoint[];
}

interface PerSpeakerBaseInput {
  freqs: number[];
  speakers: PerSpeakerInput[];
  width: number;
  height: number;
  pad?: ChartPad;
  fMin?: number;
  fMax?: number;
  measureWidth?: (label: string) => number;
}

interface PerSpeakerBaseGeometry {
  empty: boolean;
  frame: PlotFrame;
  fMin: number;
  fMax: number;
  xScale: LogFreqScale;
  yScale: LinearScale;
  series: PerSpeakerSeries[];
  xTicks: AxisTicks;
  yTicks: YTick[];
}

function emptyGeometry(
  frame: PlotFrame, fMin: number, fMax: number, xScale: LogFreqScale,
  yScale: LinearScale, xTicks: AxisTicks,
): PerSpeakerBaseGeometry {
  return { empty: true, frame, fMin, fMax, xScale, yScale, series: [], xTicks, yTicks: [] };
}

export type PerSpeakerFreqResponseInput = PerSpeakerBaseInput;
export type PerSpeakerFreqResponseGeometry = PerSpeakerBaseGeometry;

export function computePerSpeakerFreqResponseGeometry(
  opts: PerSpeakerFreqResponseInput,
): PerSpeakerFreqResponseGeometry {
  const frame = createPlotFrame(opts.width, opts.height, opts.pad ?? DEFAULT_CHART_PAD);
  const fMin = opts.fMin ?? 20;
  const fMax = opts.fMax ?? 200;
  const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;
  const xTicks = computeXAxisTicks(fMin, fMax, xScale, measureWidth);

  const withMag = opts.speakers.filter((s) => s.mag?.length);
  if (!opts.freqs.length || !withMag.length || frame.plotW <= 0) {
    return emptyGeometry(frame, fMin, fMax, xScale, createLinearScale(frame.plotH, 0, 15, true), xTicks);
  }

  const dbBySpeaker = withMag.map((s) => magToDb(s.mag!));
  const yDomain = dbViewportFromSeries(dbBySpeaker);
  const yScale = createLinearScale(frame.plotH, yDomain.lo, yDomain.hi, true);

  const series: PerSpeakerSeries[] = withMag.map((s, i) => ({
    index: s.index,
    subkind: s.subkind,
    points: mapSeriesToPlot({ x: opts.freqs, y: dbBySpeaker[i]! }, xScale, yScale),
  }));

  const { values: yTickVals, majorInterval } = niceLinearTicks(yDomain.lo, yDomain.hi, 8);
  const yTicks: YTick[] = yTickVals.map((v) => ({
    value: v, y: linearToPlot(yScale, v), major: Math.round(v) % majorInterval === 0, label: `${Math.round(v)}`,
  }));

  return { empty: false, frame, fMin, fMax, xScale, yScale, series, xTicks, yTicks };
}

export type PerSpeakerPhaseInput = PerSpeakerBaseInput & { unit?: 'deg' | 'rad' };
export type PerSpeakerPhaseGeometry = PerSpeakerBaseGeometry & { unit: 'deg' | 'rad' };

const RAD_TO_DEG = 180 / Math.PI;

export function computePerSpeakerPhaseGeometry(opts: PerSpeakerPhaseInput): PerSpeakerPhaseGeometry {
  const frame = createPlotFrame(opts.width, opts.height, opts.pad ?? DEFAULT_CHART_PAD);
  const fMin = opts.fMin ?? 20;
  const fMax = opts.fMax ?? 200;
  const unit = opts.unit ?? 'deg';
  const xScale = createLogFreqScale(frame.plotW, fMin, fMax);
  const measureWidth = opts.measureWidth ?? defaultMeasureWidth;
  const xTicks = computeXAxisTicks(fMin, fMax, xScale, measureWidth);

  const withPhase = opts.speakers.filter((s) => s.phase?.length);
  if (!opts.freqs.length || !withPhase.length || frame.plotW <= 0) {
    return {
      ...emptyGeometry(frame, fMin, fMax, xScale, createLinearScale(frame.plotH, -180, 180, true), xTicks),
      unit,
    };
  }

  const toDisplay = (rad: number[]) => (unit === 'deg' ? rad.map((v) => v * RAD_TO_DEG) : rad);
  const unwrappedBySpeaker = withPhase.map((s) => toDisplay(unwrapPhase(s.phase!)));

  const yDomain = yDomainFromSeries(unwrappedBySpeaker.map((y) => ({ x: opts.freqs, y })));
  const yScale = createLinearScale(frame.plotH, yDomain.lo, yDomain.hi, true);

  const series: PerSpeakerSeries[] = withPhase.map((s, i) => ({
    index: s.index,
    subkind: s.subkind,
    points: mapSeriesToPlot({ x: opts.freqs, y: unwrappedBySpeaker[i]! }, xScale, yScale),
  }));

  const rawStep = (yDomain.hi - yDomain.lo) / 6;
  const step = unit === 'deg'
    ? Math.max(45, Math.round(rawStep / 45) * 45)
    : Math.max(0.5, Math.round(rawStep / 0.5) * 0.5);
  const yTicks: YTick[] = linearTicks(yDomain.lo, yDomain.hi, step).map((v) => ({
    value: v, y: linearToPlot(yScale, v), major: Math.round(v) % (2 * step) === 0,
    label: unit === 'deg' ? `${Math.round(v)}` : `${v.toFixed(1)}`,
  }));

  return { empty: false, frame, fMin, fMax, xScale, yScale, series, xTicks, yTicks, unit };
}
